import { AppWindow, KeyRound, LockKeyhole, MoveUpRight, Plug, Settings, SquareArrowOutUpRight, Store, Unplug } from 'lucide-react';
import { isValidElement, useRef, useState, type ReactNode } from 'react';
import { type AppCardProps, type AppDetails, type AppDetailsItem, type CustomSlot, type IntegracoesInfo } from './Types';
import { AnimatePresence, motion } from 'framer-motion';
import Modal from '../../ui/Modal/Modal';
import ModalTrigger from '../../ui/Modal/ModalTrigger';
import { Button } from '../../ui/Button';
import ModalContent from '../../ui/Modal/ModalContent';
import { useTranslation } from '../../lib/i18n/TranslationContext';
import { DropdownMenu } from '../../ui/DropDown/DropdownMenu';
import { DropdownMenuTrigger } from '../../ui/DropDown/DropdownMenuTrigger';
import { DropDownItem, DropdownMenuContent } from '../../ui/DropDown/DropdownMenuContent';
import { twMerge } from 'tailwind-merge';
import Boss from '../../ui/appsIcons/Boss';

const DEFAULT_CATEGORY_DESCRIPTIONS: Record<string, string> = {
	Destaques: 'Integrações em destaque para começar mais rápido.',
	Marketing: 'Aplicativos focados em promover o marketing da sua loja.',
	Comunicação: 'Ferramentas para se comunicar com seus clientes.',
	Atendimento: 'Melhore o atendimento da sua loja dando suporte para seus clientes.',
	Segurança: 'Proteja sua loja e seus dados.',
	Pagamentos: 'Receba e gerencie pagamentos da sua loja.',
	Logística: 'Organize envios e entregas.',
	Estatística: 'Acompanhe os números da sua loja.',
	Dropshipping: 'Integrações para operação de dropshipping.',
};

const EmpreenderBadge = () => {
	return (
		<div
			aria-hidden
			className='pointer-events-none absolute bottom-0 right-0 size-[25px] overflow-hidden rounded-br-[8px]'
			style={{ clipPath: 'polygon(100% 0, 0 100%, 100% 100%)' }}>
			<div className='absolute inset-0 bg-[#6B3BFF]' />
			<Boss className='absolute bottom-[2px] right-[3px] size-[10px] text-white' />
		</div>
	);
};

const actionBtnClass =
	'[&_button]:h-9 [&_button]:rounded-lg [&_button]:border [&_button]:border-gray-200 [&_button]:bg-white [&_button]:px-4 [&_button]:text-sm [&_button]:font-medium [&_button]:text-gray-900 [&_button]:shadow-none [&_a]:h-9 [&_a]:rounded-lg [&_a]:border [&_a]:border-gray-200 [&_a]:bg-white [&_a]:px-4 [&_a]:text-sm [&_a]:font-medium [&_a]:text-gray-900';

const disconnectBtnClass =
	'[&_button]:h-9 [&_button]:rounded-lg [&_button]:border-0 [&_button]:bg-transparent [&_button]:px-4 [&_button]:text-sm [&_button]:font-medium [&_button]:text-[#E01E5A] [&_button]:shadow-none [&_button]:hover:bg-red-50 [&_a]:h-9 [&_a]:border-0 [&_a]:bg-transparent [&_a]:px-4 [&_a]:text-sm [&_a]:font-medium [&_a]:text-[#E01E5A]';

const connectBtnClass =
	'rounded-lg transition-colors hover:bg-gray-100 [&_button]:pointer-events-none [&_button]:h-9 [&_button]:rounded-lg [&_button]:border-0 [&_button]:bg-transparent [&_button]:px-4 [&_button]:text-sm [&_button]:font-medium [&_button]:text-gray-900 [&_button]:shadow-none [&_a]:pointer-events-none [&_a]:h-9 [&_a]:rounded-lg [&_a]:border-0 [&_a]:bg-transparent [&_a]:px-4 [&_a]:text-sm [&_a]:font-medium [&_a]:text-gray-900';

const menuItemClass =
	'flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-gray-900 outline-none data-[highlighted]:bg-gray-100';

function truncateUrl(url: string, max = 22) {
	const cleaned = url.replace(/^https?:\/\//, '');
	if (cleaned.length <= max) return cleaned;
	return `${cleaned.slice(0, max - 3)}...`;
}

function DetalhesDropdown({
	details,
	items = [],
	installed,
	onConnect,
	onDisconnect,
	onCopyChave,
}: {
	details: AppDetails;
	items?: AppDetailsItem[];
	installed?: boolean;
	onConnect?: () => void;
	onDisconnect?: () => void;
	onCopyChave?: (chave: string) => void;
}) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);

	const copyChave = async () => {
		if (!details.chave) return;
		try {
			await navigator.clipboard.writeText(details.chave);
		} catch {
			/* ignore */
		}
		onCopyChave?.(details.chave);
	};

	const hasConnect = !installed && !!onConnect;
	const hasDisconnect = installed && !!onDisconnect;
	const showChave = installed && !!details.chave;
	const showStore = installed && !!details.storeUrl;
	const extraItems = items;
	const hasItems = !!(
		showChave ||
		showStore ||
		details.openAppHref ||
		details.plusHref ||
		extraItems.length ||
		hasConnect ||
		hasDisconnect
	);

	if (!hasItems) {
		return (
			<Button variant='outline' disabled>
				<Settings className='size-4' />
				{t('Detalhes')}
			</Button>
		);
	}

	return (
		<DropdownMenu isOpen={isOpen} setIsOpen={setIsOpen}>
			<DropdownMenuTrigger
				className={twMerge(
					'inline-flex h-9 w-auto items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50',
					isOpen && 'border-gray-300 bg-gray-50',
				)}>
				<Settings className='size-4' />
				{t('Detalhes')}
			</DropdownMenuTrigger>

			<DropdownMenuContent
				side='bottom'
				align='start'
				sideOffset={6}
				className='min-w-[220px] rounded-lg border border-gray-100 !p-1 shadow-md'>
				<div className='px-2 py-1.5 text-xs font-medium text-gray-400'>{t('Detalhes')}</div>

				{showChave && (
					<DropDownItem>
						<button type='button' className={menuItemClass} onClick={copyChave}>
							<KeyRound className='size-4 shrink-0 text-gray-900' />
							<span className='flex-1'>{t('Chave')}</span>
							<span className='max-w-[96px] truncate text-gray-400'>{details.chave}</span>
						</button>
					</DropDownItem>
				)}

				{showStore && (
					<DropDownItem>
						<a
							href={details.storeUrl!.startsWith('http') ? details.storeUrl! : `https://${details.storeUrl}`}
							target='_blank'
							rel='noopener noreferrer'
							className={menuItemClass}>
							<Store className='size-4 shrink-0 text-gray-900' />
							<span className='truncate'>{truncateUrl(details.storeUrl!)}</span>
						</a>
					</DropDownItem>
				)}

				{details.openAppHref && (
					<DropDownItem>
						<a href={details.openAppHref} target='_blank' rel='noopener noreferrer' className={menuItemClass}>
							<AppWindow className='size-4 shrink-0 text-gray-900' />
							<span>{t('Abrir App')}</span>
						</a>
					</DropDownItem>
				)}

				{details.plusHref && (
					<DropDownItem>
						<a href={details.plusHref} target='_blank' rel='noopener noreferrer' className={menuItemClass}>
							<SquareArrowOutUpRight className='size-4 shrink-0 text-gray-900' />
							<span>{t('Empreender Plus')}</span>
						</a>
					</DropDownItem>
				)}

				{extraItems.map((item) => (
					<DropDownItem key={item.label} className={item.danger ? 'text-[#E01E5A] data-[highlighted]:bg-red-50' : undefined}>
						<button
							type='button'
							className={twMerge(menuItemClass, item.danger && '!text-[#E01E5A] data-[highlighted]:bg-red-50')}
							onClick={() => {
								setIsOpen(false);
								item.onClick();
							}}>
							{item.icon ? <span className='inline-flex size-4 shrink-0 items-center justify-center'>{item.icon}</span> : null}
							<span>{item.label}</span>
						</button>
					</DropDownItem>
				))}

				{hasConnect && (
					<DropDownItem>
						<button type='button' className={menuItemClass} onClick={onConnect}>
							<Plug className='size-4 shrink-0 text-gray-900' />
							<span>{t('Conectar')}</span>
						</button>
					</DropDownItem>
				)}

				{hasDisconnect && (
					<DropDownItem className='text-[#E01E5A] data-[highlighted]:bg-red-50'>
						<button
							type='button'
							className={twMerge(menuItemClass, '!text-[#E01E5A] data-[highlighted]:bg-red-50')}
							onClick={onDisconnect}>
							<Unplug className='size-4 shrink-0' />
							<span>{t('Desconectar')}</span>
						</button>
					</DropDownItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

const AppCard = ({
	title,
	description,
	icon,
	color,
	breveIcon = false,
	appEmpreender = false,
	installBtn,
	unistallBtn,
	modalContent,
	installed,
	extraBtn,
	customDetails,
	customSlot,
	expired = false,
	trialHref,
	upgradeHref,
	details,
	onCopyChave,
}: AppCardProps) => {
	const { t } = useTranslation();
	const connectRef = useRef<HTMLDivElement>(null);
	const disconnectRef = useRef<HTMLDivElement>(null);
	const needsUpgrade = Boolean(upgradeHref) && !installed && !breveIcon;
	const canConnect = !installed && !breveIcon && (!!installBtn || needsUpgrade);
	const canDisconnect = installed && !breveIcon && !!unistallBtn;

	const clickAction = (ref: React.RefObject<HTMLDivElement | null>) => {
		const trigger = ref.current?.querySelector<HTMLElement>('button, a, [role="button"]');
		trigger?.click();
	};

	const openConnect = () => {
		if (upgradeHref) {
			window.open(upgradeHref, '_blank', 'noopener,noreferrer');
			return;
		}
		clickAction(connectRef);
	};
	const openDisconnect = () => clickAction(disconnectRef);
	const slot = resolveCustomSlot(customSlot);

	const detailsBtn = details || customDetails?.length || canConnect || canDisconnect ? (
		<DetalhesDropdown
			details={details ?? {}}
			items={customDetails}
			installed={installed}
			onConnect={canConnect ? openConnect : undefined}
			onDisconnect={canDisconnect ? openDisconnect : undefined}
			onCopyChave={onCopyChave}
		/>
	) : extraBtn ? (
		extraBtn
	) : modalContent ? (
		<Modal>
			<ModalTrigger>
				<Button variant='outline'>
					<Settings className='size-4' />
					{t('Detalhes')}
				</Button>
			</ModalTrigger>
			<ModalContent className='w-[80vw]'>{modalContent}</ModalContent>
		</Modal>
	) : null;

	const action = installed ? unistallBtn : installBtn;

	return (
		<div
			role={canConnect ? 'button' : undefined}
			tabIndex={canConnect ? 0 : undefined}
			onClick={
				canConnect
					? (e) => {
							if ((e.target as HTMLElement).closest('[data-integracao-stop]')) return;
							openConnect();
						}
					: undefined
			}
			onKeyDown={
				canConnect
					? (e) => {
							if (e.key !== 'Enter' && e.key !== ' ') return;
							e.preventDefault();
							openConnect();
						}
					: undefined
			}
			className={twMerge(
				'flex h-full min-h-[152px] w-full flex-col gap-5 rounded-xl border border-gray-200 bg-white p-4 outline-none',
				canConnect && 'cursor-pointer transition-colors hover:border-gray-300 focus-visible:ring-2 focus-visible:ring-gray-300',
			)}>
			<div className='flex min-h-0 flex-1 items-start gap-3'>
				<div
					className='relative flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-50'
					style={color ? { backgroundColor: color } : undefined}>
					{icon}
					{appEmpreender && <EmpreenderBadge />}
				</div>

				<div className='flex min-w-0 flex-1 flex-col gap-1'>
					<div className='flex items-start justify-between gap-2'>
						<h2 className='min-w-0 truncate text-base font-medium text-gray-900'>{title}</h2>
						{(trialHref || expired || upgradeHref || slot.topRight) && (
							<div data-integracao-stop className='flex shrink-0 items-center gap-1.5' onClick={(e) => e.stopPropagation()}>
								{trialHref && (
									<a
										data-integracao-stop
										href={trialHref}
										target='_blank'
										rel='noopener noreferrer'
										onClick={(e) => e.stopPropagation()}
										className='inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200'>
										{t('Teste grátis')}
										<MoveUpRight className='size-3' />
									</a>
								)}
								{expired && (
									<span className='inline-flex items-center rounded-full bg-[#FDE8EC] px-2.5 py-1 text-xs font-medium text-[#E01E5A]'>
										{t('Expirada')}
									</span>
								)}
								{upgradeHref && (
									<span
										title={t('Upgrade necessário')}
										aria-label={t('Upgrade necessário')}
										className='inline-flex items-center justify-center rounded-lg bg-gray-100 px-2.5 py-1 text-gray-700'>
										<LockKeyhole className='size-3.5' />
									</span>
								)}
								{slot.topRight ? slot.node : null}
							</div>
						)}
					</div>
					<p className='line-clamp-2 text-sm font-normal leading-5 text-gray-500'>{description}</p>
				</div>
			</div>

			{!breveIcon && (
				<div className='flex shrink-0 items-center justify-between gap-2'>
					{detailsBtn || (!slot.topRight && slot.node) ? (
						<div data-integracao-stop className='flex items-center gap-2' onClick={(e) => e.stopPropagation()}>
							{detailsBtn ? <div className={actionBtnClass}>{detailsBtn}</div> : null}
							{!slot.topRight && slot.node ? <div className='flex items-center gap-1.5'>{slot.node}</div> : null}
						</div>
					) : (
						<span />
					)}
					{action && (
						<div
							ref={installed ? disconnectRef : connectRef}
							data-integracao-stop={installed ? true : undefined}
							className={installed ? disconnectBtnClass : connectBtnClass}
							onClick={installed ? (e) => e.stopPropagation() : undefined}>
							{action}
						</div>
					)}
				</div>
			)}

			{breveIcon && (
				<div className='shrink-0'>
					<span className='rounded bg-blue-50 px-1 py-0 text-[0.625rem] font-medium text-blue-500'>{t('Em breve')}</span>
				</div>
			)}
		</div>
	);
};

function isCustomSlot(slot: AppCardProps['customSlot']): slot is CustomSlot {
	return !!slot && typeof slot === 'object' && !isValidElement(slot) && 'children' in slot;
}

function resolveCustomSlot(slot: AppCardProps['customSlot']): { node: ReactNode; topRight: boolean } {
	if (isCustomSlot(slot)) {
		return { node: slot.children, topRight: slot.position === 'top-right' };
	}
	return { node: slot, topRight: false };
}

const cardTransition = { duration: 0.15, ease: [0.25, 0.1, 0.25, 1] as const };

function CardsGrid({ items }: { items: AppCardProps[] }) {
	const { t } = useTranslation();

	if (items.length <= 0) {
		return (
			<p className='text-sm font-normal leading-[18px] text-gray-500'>
				{t('Não encontramos resultados para sua pesquisa. Explore nossas integrações disponíveis para encontrar o que você precisa.')}
			</p>
		);
	}

	return (
		<div className='grid w-full grid-cols-[repeat(auto-fill,minmax(min(100%,388px),1fr))] gap-4'>
			<AnimatePresence mode='popLayout' initial={false}>
				{items.map((info, index) => (
					<motion.div
						key={info.key ?? `${info.title}-${index}`}
						className='w-full min-w-0'
						initial={{ opacity: 0, y: 6 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -4 }}
						transition={cardTransition}>
						<AppCard {...info} />
					</motion.div>
				))}
			</AnimatePresence>
		</div>
	);
}

function Section({
	title,
	description,
	children,
}: {
	title: string;
	description?: string;
	children: ReactNode;
}) {
	return (
		<section className='flex flex-col gap-4'>
			<div className='flex flex-col gap-1'>
				<h3 className='text-base font-semibold text-gray-900'>{title}</h3>
				{description && <p className='text-sm font-normal leading-5 text-gray-500'>{description}</p>}
			</div>
			{children}
		</section>
	);
}

function groupByTag(items: AppCardProps[]) {
	const groups = new Map<string, AppCardProps[]>();
	for (const item of items) {
		for (const tag of item.tags ?? []) {
			if (!groups.has(tag)) groups.set(tag, []);
			groups.get(tag)!.push(item);
		}
	}
	return groups;
}

const SECTION_ORDER = [
	'Destaques',
	'Marketing',
	'Comunicação',
	'Atendimento',
	'Segurança',
	'Pagamentos',
	'Logística',
	'Estatística',
	'Dropshipping',
];

function orderedGroupEntries(groups: Map<string, AppCardProps[]>) {
	const entries = [...groups.entries()];
	return entries.sort(([a], [b]) => {
		const ia = SECTION_ORDER.indexOf(a);
		const ib = SECTION_ORDER.indexOf(b);
		if (ia === -1 && ib === -1) return a.localeCompare(b);
		if (ia === -1) return 1;
		if (ib === -1) return -1;
		return ia - ib;
	});
}

const AppsIntegracao = ({
	integracoesInfo,
	category = '*',
	grouped = false,
	categoryDescriptions,
}: IntegracoesInfo) => {
	const { t } = useTranslation();
	const copy = { ...DEFAULT_CATEGORY_DESCRIPTIONS, ...categoryDescriptions };

	if (!grouped) {
		const title = category !== '*' ? category : t('Todas as plataformas');
		const description =
			category !== '*'
				? t(copy[category] ?? '')
				: t('É necessário realizar a integração para utilizar os recursos do app.');

		return (
			<div className='flex min-w-0 flex-1 flex-col gap-10'>
				<Section title={title} description={description || undefined}>
					<CardsGrid items={integracoesInfo} />
				</Section>
			</div>
		);
	}

	const groups = orderedGroupEntries(groupByTag(integracoesInfo));

	if (groups.length === 0) {
		return (
			<div className='flex min-w-0 flex-1 flex-col gap-10'>
				<Section
					title={t('Todas as plataformas')}
					description={t('É necessário realizar a integração para utilizar os recursos do app.')}>
					<CardsGrid items={integracoesInfo} />
				</Section>
			</div>
		);
	}

	return (
		<div className='flex min-w-0 flex-1 flex-col gap-10'>
			{groups.map(([tag, items]) => (
				<Section key={tag} title={tag} description={copy[tag] ? t(copy[tag]) : undefined}>
					<CardsGrid items={items} />
				</Section>
			))}
		</div>
	);
};

export default AppsIntegracao;
